import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import styled, { keyframes } from 'styled-components';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const LogisticsContainer = styled.div`
  padding: 140px 0;
  background: #ffffff;
  min-height: 100vh;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 5%;
`;

const PageTitle = styled.h1`
  text-align: center;
  font-size: 4.5rem;
  color: #2c2c2c;
  margin-bottom: 30px;
  
  letter-spacing: -2px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, #2B4B8C, #3B82C4);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const PageSubtitle = styled.p`
  text-align: center;
  font-size: 1.4rem;
  color: #6c757d;
  margin-bottom: 120px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.7;
  
`;

// Hero Section
const HeroSection = styled.section`
  position: relative;
  height: 500px;
  border-radius: 25px;
  overflow: hidden;
  margin-bottom: 120px;
  
  &.animate-in {
    animation: ${scaleIn} 0.8s ease-out;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('https://images.unsplash.com/photo-1566576912321-d58ddd7a6088?ixlib=rb-4.0.3&auto=format&fit=crop&w=2126&q=80') no-repeat center center;
    background-size: cover;
    transition: transform 0.5s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(43, 75, 140, 0.85), rgba(59, 130, 196, 0.7));
  }
  
  &:hover::before {
    transform: scale(1.05);
  }
`;

const HeroContent = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 2;
  color: white;
  
  h2 {
    font-size: 3.5rem;
    
    margin-bottom: 25px;
    letter-spacing: -1.5px;
  }
  
  p {
    font-size: 1.3rem;
    
    line-height: 1.6;
    max-width: 600px;
    margin: 0 auto;
    opacity: 0.95;
  }
  
  @media (max-width: 768px) {
    h2 {
      font-size: 2.5rem;
    }
    
    p {
      font-size: 1.1rem;
    }
  }
`;

// Services Section
const ServicesSection = styled.section`
  margin-bottom: 120px;
  
  &.animate-in {
    .services-grid .service-card:nth-child(1) { animation: ${slideInLeft} 0.6s ease-out 0.1s both; }
    .services-grid .service-card:nth-child(2) { animation: ${slideInLeft} 0.6s ease-out 0.3s both; }
    .services-grid .service-card:nth-child(3) { animation: ${slideInLeft} 0.6s ease-out 0.5s both; }
  }
`;

const SectionTitle = styled.h2`
  font-size: 3.5rem;
  
  color: #2c2c2c;
  margin-bottom: 80px;
  text-align: center;
  letter-spacing: -1.5px;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  max-width: 1000px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background: white;
  padding: 50px 40px;
  border-radius: 20px;
  border: 1px solid rgba(43, 75, 140, 0.08);
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, #2B4B8C, #3B82C4);
    transform: scaleY(0);
    transition: transform 0.4s ease;
  }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(43, 75, 140, 0.12);
    border-color: rgba(43, 75, 140, 0.15);
  }
  
  &:hover::before {
    transform: scaleY(1);
  }
  
  .icon {
    font-size: 3rem;
    color: #2B4B8C;
    margin-bottom: 25px;
    transition: all 0.4s ease;
  }
  
  &:hover .icon {
    transform: scale(1.1);
    color: #3B82C4;
  }
  
  h3 {
    font-size: 1.8rem;
    color: #2c2c2c;
    margin-bottom: 20px;
    font-weight: 500;
    letter-spacing: -0.5px;
  }
  
  p {
    color: #6c757d;
    line-height: 1.8;
    font-size: 1.1rem;
    
    margin-bottom: 20px;
  }
  
  ul {
    margin: 20px 0;
    padding-left: 0;
    list-style: none;
    text-align: left;
    
    li {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      margin-bottom: 12px;
      color: #6c757d;
      font-size: 1rem;
      line-height: 1.6;
      
      &::before {
        content: '•';
        color: #2B4B8C;
        font-weight: bold;
        margin-top: 2px;
      }
    }
  }
`;

// Stats Section
const StatsSection = styled.section`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 80px 0;
  border-radius: 30px;
  margin-bottom: 120px;
  
  &.animate-in {
    .stats-grid .stat-item:nth-child(1) { animation: ${scaleIn} 0.6s ease-out 0.1s both; }
    .stats-grid .stat-item:nth-child(2) { animation: ${scaleIn} 0.6s ease-out 0.3s both; }
    .stats-grid .stat-item:nth-child(3) { animation: ${scaleIn} 0.6s ease-out 0.5s both; }
    .stats-grid .stat-item:nth-child(4) { animation: ${scaleIn} 0.6s ease-out 0.7s both; }
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;
  max-width: 1000px;
  margin: 0 auto;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatItem = styled.div`
  text-align: center;
  padding: 30px 20px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.05);
  transition: all 0.4s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  }
  
  .stat-icon {
    font-size: 2.5rem;
    color: #2B4B8C;
    margin-bottom: 20px;
    transition: all 0.4s ease;
  }
  
  &:hover .stat-icon {
    transform: scale(1.1);
    color: #3B82C4;
  }
  
  .number {
    font-size: 2.5rem;
    
    color: #2c2c2c;
    margin-bottom: 10px;
    letter-spacing: -1px;
    display: block;
  }
  
  h3 {
    font-size: 1.2rem;
    color: #2c2c2c;
    margin-bottom: 10px;
    font-weight: 500;
  }
  
  p {
    color: #6c757d;
    font-size: 0.95rem;
    line-height: 1.5;
    
  }
`;

// Contact Section
const ContactSection = styled.section`
  background: linear-gradient(135deg, #2B4B8C 0%, #3B82C4 100%);
  padding: 100px 0;
  border-radius: 30px;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
  
  &.animate-in {
    animation: ${fadeInUp} 0.8s ease-out;
  }
  
  h2 {
    font-size: 3rem;
    
    color: white;
    margin-bottom: 25px;
    letter-spacing: -1px;
  }
  
  p {
    font-size: 1.3rem;
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.7;
    max-width: 600px;
    margin: 0 auto 40px;
    
  }
  
  .contact-btn {
    background: rgba(255, 255, 255, 0.2);
    border: 2px solid white;
    color: white;
    padding: 15px 40px;
    border-radius: 30px;
    font-weight: 500;
    font-size: 1.1rem;
    cursor: pointer;
    transition: all 0.3s ease;
    text-decoration: none;
    display: inline-block;
    backdrop-filter: blur(10px);
    letter-spacing: 0.5px;
    
    &:hover {
      background: white;
      color: #2B4B8C;
      transform: translateY(-3px);
    }
  }
  
  @media (max-width: 768px) {
    h2 {
      font-size: 2.2rem;
    }
    
    p {
      font-size: 1.1rem;
    }
  }
`;

const Logistics = () => {
  const [animatedSections, setAnimatedSections] = useState(new Set());
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimatedSections(prev => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  return (
    <LogisticsContainer>
      <Container>
        <PageTitle>{t('logistics.title')}</PageTitle>
        <PageSubtitle>
          {t('logistics.subtitle')}
        </PageSubtitle>

        <HeroSection 
          id="hero-section" 
          data-animate
          className={animatedSections.has('hero-section') ? 'animate-in' : ''}
        >
          <HeroContent>
            <h2>{t('logistics.heroTitle')}</h2>
            <p>
              {t('logistics.heroDesc')}
            </p>
          </HeroContent>
        </HeroSection>

        <ServicesSection 
          id="services-section" 
          data-animate
          className={animatedSections.has('services-section') ? 'animate-in' : ''}
        >
          <SectionTitle>{t('logistics.servicesTitle')}</SectionTitle>
          <ServicesGrid className="services-grid">
            <ServiceCard className="service-card">
              <div className="icon">🚛</div>
              <h3>{t('logistics.srv1Title')}</h3>
              <p>
                {t('logistics.srv1Desc')}
              </p>
              <ul>
                <li>{t('logistics.srv1f1')}</li>
                <li>{t('logistics.srv1f2')}</li>
                <li>{t('logistics.srv1f3')}</li>
                <li>{t('logistics.srv1f4')}</li>
              </ul>
            </ServiceCard>

            <ServiceCard className="service-card">
              <div className="icon">📦</div>
              <h3>{t('logistics.srv2Title')}</h3>
              <p>
                {t('logistics.srv2Desc')}
              </p>
              <ul>
                <li>{t('logistics.srv2f1')}</li>
                <li>{t('logistics.srv2f2')}</li>
                <li>{t('logistics.srv2f3')}</li>
                <li>{t('logistics.srv2f4')}</li>
              </ul>
            </ServiceCard>

            <ServiceCard className="service-card">
              <div className="icon">📍</div>
              <h3>{t('logistics.srv3Title')}</h3>
              <p>
                {t('logistics.srv3Desc')}
              </p>
              <ul>
                <li>{t('logistics.srv3f1')}</li>
                <li>{t('logistics.srv3f2')}</li>
                <li>{t('logistics.srv3f3')}</li>
                <li>{t('logistics.srv3f4')}</li>
              </ul>
            </ServiceCard>
          </ServicesGrid>
        </ServicesSection>

        <StatsSection 
          id="stats-section" 
          data-animate
          className={animatedSections.has('stats-section') ? 'animate-in' : ''}
        >
          <SectionTitle>{t('logistics.statsTitle')}</SectionTitle>
          <StatsGrid className="stats-grid">
            <StatItem className="stat-item">
              <div className="stat-icon">🚛</div>
              <span className="number">50+</span>
              <h3>{t('logistics.stat1Title')}</h3>
              <p>{t('logistics.stat1Desc')}</p>
            </StatItem>

            <StatItem className="stat-item">
              <div className="stat-icon">🏢</div>
              <span className="number">5</span>
              <h3>{t('logistics.stat2Title')}</h3>
              <p>{t('logistics.stat2Desc')}</p>
            </StatItem>

            <StatItem className="stat-item">
              <div className="stat-icon">⏱️</div>
              <span className="number">24-48</span>
              <h3>{t('logistics.stat3Title')}</h3>
              <p>{t('logistics.stat3Desc')}</p>
            </StatItem>

            <StatItem className="stat-item">
              <div className="stat-icon">📊</div>
              <span className="number">99%</span>
              <h3>{t('logistics.stat4Title')}</h3>
              <p>{t('logistics.stat4Desc')}</p>
            </StatItem>
          </StatsGrid>
        </StatsSection>

        <ContactSection 
          id="contact-section" 
          data-animate
          className={animatedSections.has('contact-section') ? 'animate-in' : ''}
        >
          <h2>{t('logistics.contactTitle')}</h2>
          <p>
            {t('logistics.contactDesc')}
          </p>
          <a href="/iletisim" className="contact-btn">
            {t('logistics.contactBtn')}
          </a>
        </ContactSection>
      </Container>
    </LogisticsContainer>
  );
};

export default Logistics;
